/**
 * Companion — typed client for `/api/v1/workspaces/{id}/companion`.
 *
 * The Companion is the quiet senior-colleague layer: one page-aware presence
 * that renders the durable attention queue as a single escalating overlay.
 * This client is what the `CompanionProvider` polls.
 *
 * Spec: docs/specs/COMPANION.md.
 */

import { api } from "./api";

export type CompanionTier = "ambient" | "feed" | "interrupt";
export type CompanionAction =
  | "ack"
  | "open"
  | "act"
  | "snooze"
  | "dismiss"
  | "done";

export interface CompanionItem {
  key: string;
  /** Producer-defined source type (for example watch, job, or notification). */
  source: string;
  kind: string;
  title: string;
  detail: string;
  link: string | null;
  tier: CompanionTier;
  severity: number;
  confidence: number;
  polarity: "good" | "bad" | "neutral";
  needs_input: boolean;
  /** True when this would have interrupted but was held back by low
   *  confidence — the colleague flags "I'm not fully sure". */
  uncertain: boolean;
  context_route: string | null;
  context_object: string | null;
  /** Transparent explanation when the active persona lens re-ranked this row. */
  priority_reason: string | null;
  created_at: string;
}

export interface CompanionPresence {
  tier: CompanionTier;
  /** Items that warrant attention: decisions plus high-signal feed items.
   * Quiet ambient history is intentionally excluded. */
  count: number;
  needs_you_count: number;
  digest: string;
}

export interface CompanionResponse {
  enabled: boolean;
  briefing_lens: "balanced" | "executive" | "operator" | "steward";
  presence: CompanionPresence;
  interrupt: CompanionItem | null;
  needs_you: CompanionItem[];
  feed: CompanionItem[];
  /** Quiet items. Optional only for rolling compatibility with older APIs. */
  ambient?: CompanionItem[];
  ambient_count: number;
}

export interface CompanionQuery {
  /** Current frontend route, for the context boost. */
  route?: string | null;
  /** Open object id (model / dataset / decision), for a stronger boost. */
  object?: string | null;
}

/** Resolve the route an item should open. Keep this shared so queue shortcuts
 * outside the Companion panel follow the same drill path as the panel itself. */
export function companionItemDestination(item: CompanionItem): string | null {
  if (item.kind === "dimension_conformance_review" && item.context_object) {
    return `/hierarchies?review=${encodeURIComponent(item.context_object)}`;
  }
  return item.link;
}

export function fetchCompanion(
  workspaceId: string,
  q: CompanionQuery = {},
): Promise<CompanionResponse> {
  const params = new URLSearchParams();
  if (q.route) params.set("route", q.route);
  if (q.object) params.set("object", q.object);
  const qs = params.toString();
  return api<CompanionResponse>(
    `/api/v1/workspaces/${workspaceId}/companion${qs ? `?${qs}` : ""}`,
  );
}

export function actOnCompanionItem(
  workspaceId: string,
  body: { key: string; action: CompanionAction; snooze_minutes?: number },
): Promise<{ ok: boolean; status: string }> {
  return api<{ ok: boolean; status: string }>(
    `/api/v1/workspaces/${workspaceId}/companion/act`,
    { method: "POST", body },
  );
}
